import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import HelpIcon from '../HelpIcon';
import { formatMoneyExact } from '../../utils/format';
import { exposureIndicator, sharedReasonArgs } from './creditLimitDisplay.mjs';

// GES-15 — "Credit: exposure X of limit Y" from GET /api/partners/:id/credit-exposure (the gate's own figures,
// exact text; no browser sum). `compact` = one line for the sale form; otherwise a KPI tile (Customers profile).
// `refreshKey` re-reads after a write.
function useExposure(partnerId, refreshKey) {
  const [st, setSt] = useState({ data: null, error: null, loading: false });
  const load = useCallback(() => {
    if (!partnerId) { setSt({ data: null, error: null, loading: false }); return () => {}; }
    let live = true;
    setSt({ data: null, error: null, loading: true });
    api.getCreditExposure(partnerId)
      .then((d) => { if (live) setSt({ data: d, error: null, loading: false }); })
      .catch((e) => { if (live) setSt({ data: null, error: e.message || 'error', loading: false }); });
    return () => { live = false; };
  }, [partnerId]);
  useEffect(() => load(), [load, refreshKey]);
  return { ...st, retry: load };
}

const TONE = { crit: 'var(--st-crit, #b91c1c)', good: 'var(--st-good, #15803d)', info: 'var(--st-info, #1d4ed8)', mut: 'var(--muted, #64748b)' };

function lines(m, t) {
  const exposure = formatMoneyExact(m.exposure);
  if (m.shared) return { value: t('credit.noCreditShared'), sub: t('credit.ind.exposure', { amount: exposure }) };   // shared account = 0 credit (owner rule)
  if (m.label === 'unlimited') return { value: t('credit.ind.unlimited'), sub: t('credit.ind.exposure', { amount: exposure }) };
  if (m.state === 'cash_only' && m.label !== 'over') return { value: t('credit.ind.cash_only'), sub: t('credit.ind.exposure', { amount: exposure }) };
  const head = m.headroom ? t(m.headroom.over ? 'credit.ind.overBy' : 'credit.ind.headroom', { amount: formatMoneyExact(m.headroom.amount) }) : '';
  if (m.state === 'cash_only') return { value: t('credit.ind.ofNone', { exposure }), sub: `${t(`credit.ind.${m.label}`)} · ${head}` };
  return { value: t('credit.ind.of', { exposure, limit: formatMoneyExact(m.limit) }), sub: `${t(`credit.ind.${m.label}`)} · ${head}` };
}

export default function CreditExposureIndicator({ partnerId, compact = false, refreshKey = 0 }) {
  const { t } = useTranslation('sales');
  const { data, error, loading, retry } = useExposure(partnerId, refreshKey);
  if (!partnerId) return null;
  const m = exposureIndicator(data);
  const body = loading ? { value: t('credit.ind.loading'), sub: '' }
    : error ? { value: t('credit.ind.error'), sub: '' } : m ? lines(m, t) : null;
  if (!body) return null;
  const color = m && !loading && !error ? TONE[m.tone] : 'var(--muted, #64748b)';
  const sharedNote = data && data.shared_account ? t(...sharedReasonArgs(data.name, data.shared_account.account_name)) : '';
  const retryBtn = error && <button type="button" className="btn" style={{ padding: '2px 8px', fontSize: 11 }} onClick={retry}>{t('credit.ind.retry')}</button>;
  if (compact) {
    return (
      <div data-testid="credit-indicator" role="status" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px 8px', fontSize: 12 }}>
        <span style={{ fontWeight: 700 }}>{t('credit.ind.title')}</span>
        <span style={{ color, fontWeight: 600 }}>{body.value}</span>
        {body.sub && <span style={{ color: 'var(--muted, #64748b)' }}>{body.sub}</span>}
        {sharedNote && <span data-testid="credit-indicator-shared" style={{ flexBasis: '100%', color: 'var(--st-warn, #b45309)' }}>{sharedNote}</span>}
        {retryBtn}
      </div>
    );
  }
  return (
    <div data-testid="credit-indicator" role="status" style={{ border: '1px solid var(--line)', borderRadius: 8, padding: '10px 14px', background: 'var(--card)', minWidth: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4, display: 'flex', alignItems: 'center' }}>
        {t('credit.ind.title')}<HelpIcon helpKey="concept.credit_exposure" />
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>{body.value}</div>
      {body.sub && <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{body.sub}</div>}
      {sharedNote && <div data-testid="credit-indicator-shared" style={{ fontSize: 10.5, color: 'var(--st-warn, #b45309)', marginTop: 4, overflowWrap: 'anywhere' }}>{sharedNote}</div>}
      {m && !loading && !error && <div style={{ fontSize: 10, color: 'var(--muted2, #94a3b8)', marginTop: 2 }}>{t('credit.ind.feesNote')}</div>}
      {retryBtn}
    </div>
  );
}
